import { PERSON_AVATAR } from '../../config/assets';
import { basicPhoneRegex, formatPhoneNumber } from '../../lib/PhoneNumber';
import { composeUsername, getDisplayName } from '../../lib/DisplayName';
import {
  ContactSchema,
  ConversationOwnerSchema,
  ConversationThreadMentions,
  ConversationThreadSchema,
  GuestSchema,
  RoomCollaboratorSchema,
  RoomSchema,
  UserGuestSchema,
  UserSchema,
} from '../../definitions/schemas';
import { ConversationMentionType } from '../../enums/Conversations';
import { DSR_MENTION_SELECTOR, MESSAGE_BASE_CLASSNAME } from '../Input/helper';
import { MentionData } from '../Input/RichTextEditor';
import { t } from '../../lib/hook/useI18n';
import SnackbarStore from '../../stores/observables/SnackbarStore';
import UserStore from '../../stores/observables/UserStore';
import RoomParticipantsInterface from '../../graphql/Rooms/RoomParticipantsInterface';
import { CollaboratorType } from '../../enums/CollaboratorType';

export const buildConversationMessageClassName = (id: string) =>
  `${MESSAGE_BASE_CLASSNAME}-${id}`;

export const buildContactMention = (contact: ContactSchema): MentionData => ({
  id: contact.id,
  description: contact.email,
  entity: ConversationMentionType.Contact,
  image: contact.avatarUri || PERSON_AVATAR,
  name: getDisplayName(contact),
});

export const buildUserGuest = (owner: Partial<UserSchema>): GuestSchema => ({
  email: owner.email!,
  firstName: owner.firstName,
  id: owner.id!,
  lastName: owner.lastName,
  photo: owner.photo,
  type: CollaboratorType.User,
});

export const buildParticipantMention = (guest: GuestSchema): MentionData => ({
  id: guest.id,
  description: guest.email,
  entity: guest.type,
  image: guest.photo || PERSON_AVATAR,
  name: getDisplayName(guest),
});

export const buildCollaboratorMention = (
  collaborator: RoomCollaboratorSchema
): MentionData => ({
  id: collaborator.collaboratorId,
  description: collaborator.email,
  entity: collaborator.collaboratorType as unknown as ConversationMentionType,
  image: collaborator.photo || PERSON_AVATAR,
  name: composeUsername(collaborator as UserGuestSchema),
});

/**
 * It's required to build the thread owners as MentionData, so they will show up
 * in the mentions search, as Anyone who has posted in that Room / MAP
 */
const buildUsersFromThreads = (
  threads: ConversationThreadSchema[]
): MentionData[] => {
  if (!threads.length) {
    return [];
  }

  const data: MentionData[] = [];
  const owners: ConversationOwnerSchema[] = [];

  threads.forEach((thread) => {
    // build thread owner
    if (thread.ownerType === ConversationMentionType.User) {
      owners.push(thread.owner);
    }

    const childrenThreads = thread.children || [];
    // build child owner
    childrenThreads.forEach((child) => {
      if (child.ownerType === ConversationMentionType.User) {
        owners.push(child.owner);
      }
    });
  });

  owners.forEach((owner) => {
    data.push(buildParticipantMention(buildUserGuest(owner)));
  });

  return data;
};

const searchParticipants = async (
  search: string,
  roomId: string,
  userContactId?: string
): Promise<MentionData[]> => {
  try {
    const participants = await new RoomParticipantsInterface(
      userContactId
    ).search({ roomId, search });

    return participants?.map((participant) =>
      buildParticipantMention(participant)
    );
  } catch (e) {
    return [];
  }
};

const filterMentionsList = (
  input: string,
  list: MentionData[]
): MentionData[] => {
  const lowercasedSearch = input.trim().toLowerCase();

  return list.filter(
    ({ name, description }) =>
      `${name} ${description}`.trim().toLowerCase().indexOf(lowercasedSearch) >
      -1
  );
};

export const buildSearcherMentions = async (
  input: string,
  room: RoomSchema,
  threads: ConversationThreadSchema[],
  userContactId?: string
): Promise<MentionData[]> => {
  const participantsFormatted = await searchParticipants(
    input,
    room.id,
    userContactId
  );

  const usersFromMentionsFormatted = buildUsersFromThreads(threads || []);

  const listUsers: MentionData[] = [
    ...(participantsFormatted || []),
    ...(usersFromMentionsFormatted || []),
  ];

  // remove duplicate entities and also removes the duplicated owner
  const uniqueList = listUsers.filter(
    (obj, index, self) =>
      index ===
      self.findIndex((o) => o.id === obj.id && o.entity === obj.entity)
  );

  return filterMentionsList(input, uniqueList);
};

export const buildMentionsPayload = (
  message: string
): ConversationThreadMentions[] | undefined => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(message, 'text/html');
  const mentions = doc.querySelectorAll(`span${DSR_MENTION_SELECTOR}`);

  if (!mentions.length) {
    return;
  }

  const payload = Array.from(mentions)?.map((node) => {
    const { dataset } = node as HTMLElement;

    return {
      entityId: dataset.entityId,
      entityType: dataset.entityType,
    };
  }) as ConversationThreadMentions[];

  // remove duplicate entities
  return payload.filter(
    (obj, index, self) =>
      index ===
      self.findIndex(
        (o) => o.entityId === obj.entityId && o.entityType === obj.entityType
      )
  );
};

export const buildInitialMention = (
  room: RoomSchema | null | undefined
): MentionData | undefined => {
  if (!room) {
    return;
  }

  const currentUser = UserStore.get();
  if (currentUser) {
    return room.contact ? buildContactMention(room.contact) : undefined;
  }

  return room.owner
    ? buildParticipantMention(buildUserGuest(room.owner))
    : undefined;
};

export const displaySuccessInvite = (contact?: ContactSchema) => {
  if (!contact) {
    return;
  }

  SnackbarStore.show(
    t('mention.invite.success', { name: getDisplayName(contact) })
  );
};

// Interacts on the message nodes to detect if a mention has a phone number and replace it with a mask
export const processMessageMentions = (message: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(message, 'text/html');
  const mentions = doc.querySelectorAll(`span${DSR_MENTION_SELECTOR}`);

  mentions.forEach((mention: any) => {
    // remove @ character
    const mentionText = mention.textContent.slice(1);

    // check if mention is a mobile number
    if (basicPhoneRegex.test(mentionText)) {
      // replace mention text with maskPhoneNumber
      // eslint-disable-next-line no-param-reassign
      mention.textContent = `@${formatPhoneNumber(mentionText, true)}`;
    }
  });

  // Serialize the modified DOM document back to an HTML string
  const serializer = new XMLSerializer();
  const modifiedHtmlString = serializer.serializeToString(doc);

  return modifiedHtmlString;
};
